import React, { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { generateReportPdf, buildReportPdfName } from '../../utils/reportPdf';
import theme from '../../theme';
import { topbarTokens as t } from '../Topbar/tokens';
import { withLanguage } from '../common/Texts';
import useReportData from './useReportData';
import ReportHeader from './ReportHeader';
import ReportInfoGrid from './ReportInfoGrid';
import ReportChart from './ReportChart';
import ReportAnalysis from './ReportAnalysis';
import ReportPending from './ReportPending';

export default function Report() {
    const { id, type } = useParams();
    const language = useSelector((state) => state.user.language);
    const tr = useMemo(() => withLanguage(language), [language]);
    const reportContentRef = useRef(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);

    const { data, loading, pending, error } = useReportData(id, type);

    const handleDownloadPdf = async () => {
        const element = reportContentRef.current;
        if (!element || generatingPdf) return;

        setGeneratingPdf(true);
        try {
            await generateReportPdf(element, buildReportPdfName(data?.nombre, id));
        } catch (err) {
            console.error('Error generando el PDF:', err);
        } finally {
            setGeneratingPdf(false);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (pending) {
            return <ReportPending tr={tr} />;
        }
        if (error) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 1 }}>
                        {tr('report_error_title')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {tr('report_error_description')}
                    </Typography>
                </Box>
            );
        }
        if (data) {
            return (
                <>
                    <ReportHeader tr={tr} />
                    <ReportInfoGrid
                        nombre={data.nombre}
                        edad={data.edad}
                        posicion={data.posicion}
                        fecha_test={data.fecha_test}
                        currentCapacity={data.currentCapacity}
                        match_persona_rol={data.match_persona_rol}
                        potentialCapacity={data.potentialCapacity}
                        futurePotential={data.futurePotential}
                        tr={tr}
                    />
                    <ReportChart
                        language={language}
                        companyChart={data.company.chart}
                        curva={data.curva}
                        age={data.edad}
                        ageAtReply={data.age_at_reply}
                    />
                    <ReportAnalysis
                        reporte={data.reporte}
                        tr={tr}
                    />
                </>
            );
        }
        return null;
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.paper', py: 4 }}>
                <Container maxWidth={false} sx={{ width: '90%' }}>
                    {data && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button
                                size="small"
                                startIcon={generatingPdf
                                    ? <CircularProgress size={16} sx={{ color: t.color.fg }} />
                                    : <FileDownloadOutlinedIcon fontSize="small" />}
                                disabled={generatingPdf}
                                onClick={handleDownloadPdf}
                                sx={{
                                    color: t.color.fg,
                                    bgcolor: t.color.surface,
                                    border: `1px solid ${t.color.border}`,
                                    borderRadius: `${t.radius.md}px`,
                                    px: 1.5,
                                    py: 0.75,
                                    '&:hover': { bgcolor: t.color.bgSub },
                                }}
                            >
                                {tr('download_pdf')}
                            </Button>
                        </Box>
                    )}
                    <Box sx={{ p: 4 }} ref={reportContentRef}>
                        {renderContent()}
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
